import { ForbiddenError } from '@casl/ability';
import castArray from 'lodash/castArray.js';
import find from 'lodash/find.js';
import get from 'lodash/get.js';
import has from 'lodash/has.js';
import some from 'lodash/some.js';

import Gate from '@/plugins/vue-gate/Gate.js';
import GateKeeper from '@/plugins/vue-gate/GateKeeper.js';
import { ability } from '@/store/Ability.js';

class CanVisitMiddleware {
    /**
     * @param store
     * @param router
     */
    constructor(store, router) {
        this.store = store;
        this.router = router;

        this.user = this.store.getters['Auth/user'];

        this.gate = new Gate().forUser(this.user);

        this.gateKeeper = new GateKeeper().forUser(this.user);
    }

    static specialPermissions() {
        return [
            {
                action: 'edit',
                subject: 'course',
                handler: 'checkCourseGroupForUser',
            },
            {
                action: 'view',
                subject: 'course dashboard',
                handler: 'checkCourseGroupForUser',
            },
            {
                action: 'view',
                subject: 'gradebook preview',
                handler: 'checkCourseGroupForUser',
            },
            {
                action: 'view',
                subject: 'course enrollment page',
                handler: 'checkCourseGroupForUser',
            },
            {
                action: 'view',
                subject: 'help',
                handler: 'checkSupportDocumentationEnabledForInstitution',
            },
            {
                action: 'create',
                subject: 'message category',
                handler: 'checkInboxEnabled',
            },
            {
                action: 'view',
                subject: 'meeting',
                handler: 'checkIsStudentSupport',
            },
            {
                action: 'view all',
                subject: 'attendances',
                handler: 'checkIsStudentSupport',
            },
        ];
    }

    isSpecialRoute(action, subject) {
        return get(
            find(CanVisitMiddleware.specialPermissions(), permission => {
                return permission.action === action && permission.subject === subject;
            }),
            'handler'
        );
    }

    /**
     * @param to
     * @param from
     * @param permissions
     * @returns {Promise<any>}
     */
    fire(to, from, permissions) {
        castArray(permissions).forEach(permission => {
            if (typeof permission !== 'function') {
                const specialRoute = this.isSpecialRoute(permission.action, permission.subject);

                if (specialRoute) {
                    if (this[specialRoute](to, from)) {
                        return new Promise(resolve => resolve(true));
                    }
                }

                ForbiddenError.from(this.store.state.ability).throwUnlessCan(permission.action, permission.subject);
            } else if (!permission({ gateKeeper: this.gateKeeper, gate: this.gate, ability, to, from })) {
                throw new ForbiddenError('Permission denied');
            }
        });
    }

    checkCourseGroupForUser(to) {
        const isStudentSupportViaGroup = some(this.user.user_groups, userGroup => {
            return userGroup.role.name === 'Student Support';
        });

        if (this.store.getters['User/hasRole']('Student Support') || isStudentSupportViaGroup) return true;

        if (has(this.user, 'user_groups') && this.user.user_groups.length > 0) {
            return some(this.user.user_groups, userGroup => {
                const userIsNotStudent = !['Student', 'Teaching Assistant'].includes(userGroup.role.name);
                const groupIncludesCourse = find(
                    userGroup.group.courses,
                    courseGroup => Number(courseGroup.course_id) === Number(to.params.courseId)
                );

                return userIsNotStudent && groupIncludesCourse;
            });
        }
    }

    checkSupportDocumentationEnabledForInstitution() {
        if (
            this.store.getters['User/hasRole']('Admin') ||
            this.store.getters['User/hasRole']('Owner') ||
            this.store.getters['User/hasRole']('Technical Writer')
        ) {
            return true;
        }
        return this.store.getters['InstitutionSettings/get']('support_documentation_enabled');
    }

    checkInboxEnabled() {
        if (this.$can('view', 'inbox')) {
            return true;
        }

        return this.store.getters['InstitutionSettings/get']('inbox_enabled');
    }

    async checkIsStudentSupport() {
        if (this.store.getters['User/hasRole']('Student Support')) return true;

        return some(this.user.user_groups, userGroup => {
            return userGroup.role.name === 'Student Support';
        });
    }

    $can(action, subject) {
        const { permissions } = this.store.state.Auth;
        const filteredPermissions = permissions.filter(permission => {
            return permission.action === action && permission.subject === subject;
        });

        return filteredPermissions.length > 0;
    }
}

export default CanVisitMiddleware;
